import {
  TextField,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  FlexColumn,
} from '@elementary/common-ui/components';
import { useFormContext } from 'react-hook-form';
import { WarehouseType } from '@elementary/common-ui/types';
import { EnvironmentForm } from 'shared/types';
import {
  getAssetSourceRules,
  getWarehouseTerms,
  readErrorMessage,
} from './utils';

type Props = {
  index: number;
  warehouseType: WarehouseType;
  isExisting: boolean;
  disabled?: boolean;
};

type DbtProjectFieldsInfo = {
  isDatabaseRequired: boolean;
  databaseLabel: string | null;
  databasePlaceholder: string | null;
  schemaLabel: string;
  schemaPlaceholder: string;
};

const getElementarySchemaPlaceholder = (warehouseType: WarehouseType) => {
  switch (warehouseType) {
    case 'bigquery':
      return 'e.g. my_dataset_elementary';
    case 'dremio':
      return 'e.g. path/to/storage';
    default:
      return 'e.g. [schema name]_elementary';
  }
};

const getDbtProjectFieldsInfoByWarehouseType = (
  warehouseType: WarehouseType,
): DbtProjectFieldsInfo => {
  const { databaseLabel, databasePlaceholder, schemaNoun } =
    getWarehouseTerms(warehouseType);
  const { hasDatabase, isDbtDatabaseRequired } =
    getAssetSourceRules(warehouseType);
  return {
    isDatabaseRequired: isDbtDatabaseRequired,
    databaseLabel: hasDatabase ? databaseLabel : null,
    databasePlaceholder: hasDatabase ? databasePlaceholder : null,
    schemaLabel: `Elementary ${schemaNoun}`,
    schemaPlaceholder: getElementarySchemaPlaceholder(warehouseType),
  };
};

const DbtProjectFields = ({
  index,
  warehouseType,
  isExisting,
  disabled,
}: Props) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<EnvironmentForm>();

  const {
    isDatabaseRequired,
    databaseLabel,
    databasePlaceholder,
    schemaLabel,
    schemaPlaceholder,
  } = getDbtProjectFieldsInfoByWarehouseType(warehouseType);

  const sourceErrors = errors.assetSources?.[index];
  const nameError = readErrorMessage(sourceErrors?.name);
  const databaseError = readErrorMessage(
    sourceErrors && 'database' in sourceErrors
      ? sourceErrors.database
      : undefined,
  );
  const schemaError = readErrorMessage(
    sourceErrors && 'schema' in sourceErrors ? sourceErrors.schema : undefined,
  );

  return (
    <FlexColumn gap={2}>
      <FormField title="Asset source name" errorMessage={nameError}>
        <TextField
          {...register(`assetSources.${index}.name`, {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. jaffle_shop"
          disabled={disabled}
          size="large"
        />
      </FormField>

      {databaseLabel && (
        <FormField
          title={databaseLabel}
          optional={!isDatabaseRequired}
          errorMessage={databaseError}
        >
          <TextField
            {...register(
              `assetSources.${index}.database`,
              isDatabaseRequired
                ? {
                    required: requiredLabel,
                  }
                : undefined,
            )}
            variant="outlined"
            placeholder={databasePlaceholder ?? undefined}
            disabled={disabled || isExisting}
            size="large"
          />
        </FormField>
      )}

      <FormField title={schemaLabel} errorMessage={schemaError}>
        <TextField
          {...register(`assetSources.${index}.schema`, {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder={schemaPlaceholder}
          disabled={disabled || isExisting}
          size="large"
        />
      </FormField>
    </FlexColumn>
  );
};

export default DbtProjectFields;
